import { useEffect, useState } from 'react'

// Tailwind breakpoints
// Make sure these match your Tailwind config, otherwise will not work as expected
export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
  xs: '480px',
}

export type Breakpoint = keyof typeof breakpoints

export const useTailwindBreakpoint = (
  breakpoint: Breakpoint,
  initialValue = false,
  targetGetter?: () => unknown
) => {
  const [isAboveBreakpoint, setIsAboveBreakpoint] = useState(initialValue)

  useEffect(() => {
    let targetElement: HTMLElement | undefined

    // check if targetGetter is yields an HTMLElement
    if (targetGetter) {
      const element = targetGetter()

      if (element && element instanceof HTMLElement) {
        targetElement = element
      } else {
        console.warn(
          `[useTailwindBreakpoint]: Unexpected target type: ${typeof element}. Expected HTMLElement. Falling back to window.`
        )
        targetElement = undefined
      }
    }

    // if target is an element we have to resort to ResizeObserver (there's only matchMedia for window)
    if (targetElement) {
      const resizeObserver = new ResizeObserver(([entry]) => {
        if (!entry) return

        const width = entry.contentRect.width
        setIsAboveBreakpoint(width >= parseInt(breakpoints[breakpoint], 10))
      })

      resizeObserver.observe(targetElement)

      return () => {
        resizeObserver.disconnect()
      }
    }

    // otherwise, target is window and we can use window.matchMedia
    const mediaQuery = window.matchMedia(
      `(min-width: ${breakpoints[breakpoint]})`
    )

    function handleChange(e: MediaQueryListEvent | MediaQueryList) {
      setIsAboveBreakpoint(e.matches)
    }

    handleChange(mediaQuery)
    mediaQuery.addEventListener('change', handleChange)

    return () => {
      mediaQuery.removeEventListener('change', handleChange)
    }
  }, [breakpoint, targetGetter])

  return isAboveBreakpoint
}
